/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
// TODO: Fix accessibility

import {
  DOMAttributes,
  MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { IMAGES_REPO } from '../../../../static-resources';
import { formatNumber } from '../../../../utils/formatting/number-formatter.helper';
import CoefficientTooltip from './coefficient-tooltip.component';
import { CoefficientHelp } from './coefficient-v2.component';
import './coefficient-card.component.scss';

interface CoefficientCardProps extends DOMAttributes<HTMLDivElement> {
  title: string;
  value: number | string;
  className?: string;
  cardClassName?: string;
  active?: boolean;
  brands?: Record<string, any>[];
  onCompBrandClick?(i: Record<string, any>): void;
  selectedBrand?: string[];
  coefficientHelp?: CoefficientHelp;
}

export const CoefficientCardComponent = ({
  title,
  value,
  cardClassName,
  active,
  brands,
  onCompBrandClick,
  selectedBrand,
  coefficientHelp,
  ...rest
}: CoefficientCardProps) => {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const iconRef = useRef<HTMLSpanElement>(null); // Ref for the tooltip icon
  const hideTimeout = useRef<number | null>(null); // Timeout ref to handle delayed hiding

  const getThresholdClassName = useCallback((value: any) => {
    if (value !== undefined && typeof value === 'number') {
      if (value > 1) {
        return 'higher';
      }
      if (value > 0.5) {
        return 'moderate';
      }
      if (value <= 0.5) {
        return 'lower';
      }
    } else {
      return '';
    }
  }, []);
  const cardThresholdClassName = useMemo(() => {
    if (brands) {
      return '';
    } else {
      return getThresholdClassName(value);
    }
  }, [value, getThresholdClassName, brands]);

  const handleCompBrandClick = useCallback(
    (i: Record<string, any>) => {
      if (onCompBrandClick) {
        onCompBrandClick(i);
      }
    },
    [onCompBrandClick],
  );

  const calculateTooltipPosition = (event: MouseEvent) => {
    const icon = event.currentTarget as HTMLSpanElement;
    const rect = icon.getBoundingClientRect(); // Get the position of the hovered icon
    setTooltipPosition({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  };

  const handleMouseLeave = () => {
    setTimeout(() => {
      setShowTooltip(null);
    }, 200); // Add 200ms delay
  };

  const handleMouseEnter = (tooltipType: string, event: MouseEvent) => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current); // Clear any existing timeout to prevent immediate hiding
    }
    setShowTooltip(tooltipType);
    calculateTooltipPosition(event);
  };

  return (
    <div {...rest} className="e-coefficient-wrapper">
      <div
        className={`e-coefficient ${cardClassName ?? ''} ${cardThresholdClassName} ${active ? 'active' : ''}`}
      >
        <div className="coefficient-title saperatecard text-center text-size-small">
          {title}
        </div>

        {!brands && (
          <div className="text-center">
            <h5 className="text-center">
              {typeof value === 'number' &&
                formatNumber(value, {
                  maximumFractionDigits: 4,
                  minimumFractionDigits: 4,
                })}
              {typeof value === 'string' && value}
              <span
                className="help-icon"
                ref={iconRef}
                onMouseEnter={(e) => handleMouseEnter('value', e)}
                onMouseLeave={handleMouseLeave}
              >
                <InfoCircleFill />
              </span>
            </h5>
            {showTooltip === 'value' && tooltipPosition && (
              <CoefficientTooltip
                title={coefficientHelp?.name ?? title}
                description={
                  coefficientHelp?.description || 'No description available.'
                }
                top={tooltipPosition.top}
                left={tooltipPosition.left}
              />
            )}
          </div>
        )}

        {brands && (
          <div className="coefficient-brand-container">
            {brands.map((i, index) => (
              <div
                key={index}
                className={`coefficient-brand-item e-coefficient ${cardClassName ?? ''} ${getThresholdClassName(i.value)} ${
                  selectedBrand && selectedBrand.indexOf(i.id) !== -1
                    ? 'active'
                    : ''
                }`}
                onClick={(e) => {
                  handleCompBrandClick(i);
                  e.stopPropagation();
                }}
              >
                <div className="coefficient-brand-name">
                  {/* <span className="bubble-level"></span> */}
                  {i.name}
                </div>
                <div className="coefficient-brand-value">
                  {typeof i.value === 'number' &&
                    formatNumber(i.value, {
                      maximumFractionDigits: 4,
                      minimumFractionDigits: 4,
                    })}
                </div>
              </div>
            ))}

            <span
              className="help-icon"
              ref={iconRef}
              onMouseEnter={(e) => handleMouseEnter('brands', e)}
              onMouseLeave={handleMouseLeave}
            >
              <InfoCircleFill />
            </span>

            {showTooltip === 'brands' && tooltipPosition && (
              <CoefficientTooltip
                title={coefficientHelp?.name ?? title}
                description={
                  coefficientHelp?.description || 'No description available.'
                }
                top={tooltipPosition.top}
                left={tooltipPosition.left}
              />
            )}
          </div>
        )}

        <div className="drag-icon" draggable={false}>
          <div
            className={`drag-icon-content  ${cardThresholdClassName}`}
            draggable={false}
          >
            <img alt="" src={IMAGES_REPO.DragIcon} draggable={false} />
          </div>
        </div>
      </div>
    </div>
  );
};
